import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import websiteSubscriptionService from '../../services/websiteSubscriptionService';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './WebsiteSubscriptionGuard.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const SubscriptionRequired = ({ onSubscriptionSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);

  const handleSubscribe = () => {
    setShowCheckout(true);
  };

  return (
    <div className="subscription-required-container">
      <div className="subscription-required-card">
        <div className="subscription-icon">
          🌐
        </div>
        <h2>Website Add-On Required</h2>
        <p>
          The "My Website" feature is available as a premium add-on for agency agents.
          Subscribe for just <strong>$4.99/month</strong> to create and manage your personalized agent website.
        </p>
        
        <div className="subscription-features">
          <h3>What's included:</h3>
          <ul>
            <li>✅ Personalized agent website</li>
            <li>✅ Custom branding and themes</li>
            <li>✅ Content management system</li>
            <li>✅ Blog and promotions</li>
            <li>✅ Client-facing booking forms</li>
            <li>✅ SEO optimization</li>
          </ul>
        </div>

        {!showCheckout ? (
          <div className="subscription-actions">
            <button 
              className="subscribe-button"
              onClick={handleSubscribe}
              disabled={loading}
            >
              Subscribe Now - $4.99/month
            </button>
            <p className="subscription-note">
              Cancel anytime. No long-term commitment required.
            </p>
          </div>
        ) : (
          <Elements stripe={stripePromise}>
            <CheckoutForm 
              onSuccess={onSubscriptionSuccess}
              onCancel={() => setShowCheckout(false)}
            />
          </Elements>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

const CheckoutForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) return;
    
    setLoading(true);
    setError('');

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error: paymentError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (paymentError) {
        setError(paymentError.message);
        setLoading(false);
        return;
      }

      // Subscribe with payment method
      const result = await websiteSubscriptionService.subscribe(paymentMethod.id);
      
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Subscription failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <div className="card-element-container">
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
            },
          }}
        />
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="checkout-actions">
        <button 
          type="button" 
          onClick={onCancel}
          className="cancel-button"
          disabled={loading}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          disabled={!stripe || loading}
          className="subscribe-button"
        >
          {loading ? 'Processing...' : 'Subscribe $4.99/month'}
        </button>
      </div>
    </form>
  );
};

const WebsiteSubscriptionGuard = ({ children }) => {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const checkAccess = async () => {
    try {
      // Independent agents, super admins, and agency admins have automatic access
      if (user.role === 'independent_agent' || 
          user.role === 'super_admin' || 
          user.role === 'agency_admin') {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      // Agency agents need active subscription
      if (user.role === 'agent' && user.agency_id) {
        const status = await websiteSubscriptionService.getSubscriptionStatus();
        setSubscriptionStatus(status);
        setHasAccess(status.hasAccess);
      } else {
        setHasAccess(false);
      }
    } catch (error) {
      console.error('Error checking website access:', error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      checkAccess();
    }
  }, [user]);

  const handleSubscriptionSuccess = () => {
    setHasAccess(true);
    // Optionally refresh user data or show success message
  };

  if (loading) {
    return (
      <div className="subscription-loading">
        <div className="loading-spinner"></div>
        <p>Checking website access...</p>
      </div>
    );
  }

  if (!hasAccess) {
    return <SubscriptionRequired onSubscriptionSuccess={handleSubscriptionSuccess} />;
  }

  return children;
};

export default WebsiteSubscriptionGuard;
